import axios from 'axios';
import { setupAxiosInterceptor } from './auth/axiosConfig';
import config from './config';

// AI Employee API client — axios instance + auth interceptor (standard services/* pattern).
const aiEmployeeApi = axios.create({
  baseURL: config.API_BASE_URL + '/cellcog',
});

setupAxiosInterceptor(aiEmployeeApi);

// ===== Employee CRUD =====
export const createAiEmployee = (data) => aiEmployeeApi.post('/ai-employees', data);
export const listAiEmployees = () => aiEmployeeApi.get('/ai-employees');
export const getAiEmployee = (employeeId) => aiEmployeeApi.get(`/ai-employees/${employeeId}`);
// ONE aggregate read for the single-pane dashboard: employee + shift_state + counts + previews +
// dashboards + schedule + budget, in a single round trip. Replaces the old 6-call fan-out.
export const getEmployeeDashboard = (employeeId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/dashboard`);
export const pauseAiEmployee = (employeeId) => aiEmployeeApi.post(`/ai-employees/${employeeId}/pause`);
// CEL-2137 (pause = frozen): Resume IS the wake — the server drains the mailbox when non-empty.
// `source` = 'chat' (paused-mailbox modal / chat status bar: the owner is at the desk, the wake is
// born supervisor-present) | 'settings'. Response = employee dict + { woken, waiting }.
export const resumeAiEmployee = (employeeId, source) =>
  aiEmployeeApi.post(`/ai-employees/${employeeId}/resume`, source ? { source } : {});
export const deleteAiEmployee = (employeeId) => aiEmployeeApi.delete(`/ai-employees/${employeeId}?confirm=true`);
// Org chart Layer 1: a deleted employee waits 30 days in its owner's trash ("Recently deleted" in the sidebar).
export const listTrashedEmployees = () => aiEmployeeApi.get('/ai-employees/trash');
export const restoreAiEmployee = (employeeId) => aiEmployeeApi.post(`/ai-employees/${employeeId}/restore`);
// Org chart Layer 1: hand several AI employees (a leader and its reports) to one teammate in one step.
export const handOverEmployees = (employeeIds, newOwnerUserId, keepPreviousAsShared = false) =>
  aiEmployeeApi.post('/ai-employees/handover', {
    employee_ids: employeeIds,
    new_owner_user_id: newOwnerUserId,
    keep_previous_as_shared: keepPreviousAsShared,
  });

// ===== Schedule =====
// (onboarding is completed by the agent via its `onboarding_complete` stop, not a UI call)
export const updateSchedule = (employeeId, data) =>
  aiEmployeeApi.put(`/ai-employees/${employeeId}/schedule`, data);
export const setEmployeeSupervisor = (employeeId, supervisorEmployeeId) =>
  aiEmployeeApi.put(`/ai-employees/${employeeId}/supervisor`, { supervisor_employee_id: supervisorEmployeeId });
// Kind-agnostic manager (org_reporting_single_truth_prd.md §5a): kind 'human' (a User id) or
// 'employee' (anchor employee id); null managerId = reports to the owner directly.
export const setEmployeeManager = (employeeId, kind, managerId) =>
  aiEmployeeApi.put(`/ai-employees/${employeeId}/supervisor`, { manager_kind: kind, manager_id: managerId });
// (setShiftMode removed at the routines cutover, CEL-1694 2026-08-25 — modes retired;
// scheduling lives on routines: see listOrgRoutines/createOrgRoutine + the per-employee set.)
// Mode × Tier redesign §7: set the employee's model — (chat_mode, chat_tier) on the ANCHOR.
// Applies from the next run; Team is rejected for employees. Owner-only.
export const setEmployeeModel = (employeeId, data) =>
  aiEmployeeApi.put(`/ai-employees/${employeeId}/model`, data);
// NOTE (2026-07): updateCreditsPerShift was removed with the deprecation of `credits_per_shift`.

// ===== Shifts =====
export const listEmployeeShifts = (employeeId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/shifts`);
// Live state of the current shift: { state: working|needs_input|complete|none, can_start_now, status }
export const getEmployeeShiftState = (employeeId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/shift-state`);
// Inject an extra shift now WITHOUT touching the schedule (only when the current shift is complete).
export const startEmployeeShiftNow = (employeeId) =>
  aiEmployeeApi.post(`/ai-employees/${employeeId}/start-shift`);
// "Start now" for the next-shift stream control. The backend branches on the current shift's live
// state: concluded → starts the next shift immediately; still running/paused → requests a system
// wind-down (the shift clocks out with shift_oom, which spawns the successor immediately). Response:
// { action: "started" | "winddown_requested", state, message }.
export const startNextShift = (employeeId) =>
  aiEmployeeApi.post(`/ai-employees/${employeeId}/next-shift`);

// ===== Tasks (read-only board; the employee is the only writer of task state) =====
export const listEmployeeTasks = (employeeId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/tasks`);
export const getEmployeeTaskDetail = (employeeId, taskId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/tasks/${taskId}`);
// Global Tasks board across all of the user's employees. params: { employee_id, task_status, recurring, q }
export const listAllUserTasks = (params = {}) =>
  aiEmployeeApi.get(`/ai-employees/tasks`, { params });
// Sidebar Tasks badge: the "waiting on a human" COUNT only ({ waiting: N }) — never the full board
// (the badge previously downloaded the entire ~MB board to keep one integer).
// Optional employeeId (post-office id) narrows the count to ONE employee's board — the Employee
// Computer's per-employee Tasks badge. No arg = the user-wide sidebar badge (unchanged).
export const getTasksAttention = (employeeId) =>
  aiEmployeeApi.get(
    `/ai-employees/tasks/attention${employeeId ? `?employee_id=${encodeURIComponent(employeeId)}` : ''}`
  );
// Tasks v2 deep link: fetch a task by its display key ("CEL-123"), resolved within the viewer's
// scopes (org board first, personal second). Payload = full detail + the owning employee identity,
// so the /tasks/:taskKey modal needs no parent board.
export const getTaskByKey = (taskKey) =>
  aiEmployeeApi.get(`/ai-employees/tasks/by-key/${encodeURIComponent(taskKey)}`);

// Post a human comment on a task. `markdown` may contain SHOW_FILE tags pointing at staged human_files/...
// (the server relocates them into the task namespace + rewrites to gs://). Returns { comment, file_urls }.
export const addEmployeeTaskComment = (employeeId, taskId, markdown) =>
  aiEmployeeApi.post(`/ai-employees/${employeeId}/tasks/${taskId}/comments`, { markdown });

// Tasks v3.2 (tasks_assignment_anyone_design.md D5): a human creates a task from the board. The task
// lives on {employeeId}'s board; `assignee` is the actor token "human:<id>" / "employee:<id>" (or null).
// `wake` (CEL-2443, default true): false = a quiet hand-off to an AI employee (lands on its board for its
// next run, no wake). The response `warnings` carry a 🔔 line saying what happened to the assignee.
export const createEmployeeTaskFromBoard = (employeeId, data) =>
  aiEmployeeApi.post(`/ai-employees/${employeeId}/tasks`, data);

// Tasks v3 (CEL-1370 slice D): the human task STATE write — org members edit any org task with the
// board-owner posture. patch: { status, priority, title, detail, due_at, blocked_by, result_summary,
// assigned_to_kind, assigned_to_id, unassign, wake }. Guard rejections (e.g. closing a done_when task
// without a result summary) come back as 422 with the reason in `detail`. `wake` (CEL-2443, default
// true) only matters when the patch hands the task to an AI employee: false = no wake, its next run;
// the response `messages` carry a 🔔 line saying what happened.
export const updateEmployeeTaskState = (employeeId, taskId, patch) =>
  aiEmployeeApi.patch(`/ai-employees/${employeeId}/tasks/${taskId}`, patch);

// ===== Approvals (standing dangerous-op grants) =====
export const listEmployeeApprovals = (employeeId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/approvals`);

// ===== Activity maps (Activity Maps v1, D7: the SAME two maps the employee reads) =====
export const getEmployeeActivityMaps = (employeeId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/activity-maps`);

// ===== Dashboards (owner-facing windows) =====
export const listEmployeeDashboards = (employeeId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/dashboards`);
export const listDashboardChanges = (employeeId, dashboardId, limit = 30) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/dashboards/${dashboardId}/changes`, { params: { limit } });

// ===== Email (owner read-only view) =====
export const listEmployeeEmailThreads = (employeeId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/email/threads`);
export const getEmployeeEmailThread = (employeeId, threadId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/email/threads/${threadId}`);
// BM25-ranked email search (Atlas Search: fuzzy, subject/phrase boosted, highlighted snippets).
// params: { q, from_addr, to_addr, subject, has_attachment, channel, since, before, limit }
export const searchEmployeeEmail = (employeeId, params) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/email/search`, { params });


// ===== Global Routines board (/routines — the org-wide view) =====
export const listOrgRoutines = (employeeId, includeCompleted = false) =>
  aiEmployeeApi.get('/ai-employees/routines', {
    params: {
      ...(employeeId ? { employee_id: employeeId } : {}),
      ...(includeCompleted ? { include_completed: true } : {}),
    },
  });
export const createOrgRoutine = (data) =>
  aiEmployeeApi.post('/ai-employees/routines', data);
export const updateOrgRoutine = (routineId, data) =>
  aiEmployeeApi.patch(`/ai-employees/routines/${routineId}`, data);
export const runOrgRoutineNow = (routineId) =>
  aiEmployeeApi.post(`/ai-employees/routines/${routineId}/run-now`);
export const deleteOrgRoutine = (routineId) =>
  aiEmployeeApi.delete(`/ai-employees/routines/${routineId}`);

// ===== Routines (CEL-1694 — named time triggers + standing briefs; the WHEN layer) =====
export const listEmployeeRoutines = (employeeId, includeCompleted = false) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/routines`, {
    params: includeCompleted ? { include_completed: true } : {},
  });
export const createEmployeeRoutine = (employeeId, data) =>
  aiEmployeeApi.post(`/ai-employees/${employeeId}/routines`, data);
export const updateEmployeeRoutine = (employeeId, routineId, data) =>
  aiEmployeeApi.patch(`/ai-employees/${employeeId}/routines/${routineId}`, data);
export const runEmployeeRoutineNow = (employeeId, routineId) =>
  aiEmployeeApi.post(`/ai-employees/${employeeId}/routines/${routineId}/run-now`);
export const deleteEmployeeRoutine = (employeeId, routineId) =>
  aiEmployeeApi.delete(`/ai-employees/${employeeId}/routines/${routineId}`);

// ===== Wake conditions (what wakes the employee) =====
// Uniform list (system built-ins + owner-added Composio wake conditions) + the add/manage flow.
export const listEmployeeWakeConditions = (employeeId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/wake-conditions`);
export const listAvailableWakeConditions = (employeeId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/wake-conditions/available`);
export const getWakeConditionSchema = (employeeId, triggerSlug) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/wake-conditions/available/${encodeURIComponent(triggerSlug)}/schema`);
// Triggers v1 (agent_docs/triggers/TRIGGERS_V1_CONTRACT.md §6): one trigger TYPE for the enable card / page:
// {slug, toolkit_slug, toolkit_name, logo_url, name, description, type, config, toolkit_connected,
//  existing_condition_id, employee_name}; 404 for an unknown slug. The card creates via createWakeCondition.
export const getTriggerType = (employeeId, triggerSlug) =>
  aiEmployeeApi.get(
    `/ai-employees/${encodeURIComponent(employeeId)}/wake-conditions/trigger-types/${encodeURIComponent(triggerSlug)}`
  );
export const createWakeCondition = (employeeId, data) =>
  aiEmployeeApi.post(`/ai-employees/${employeeId}/wake-conditions`, data);
export const updateWakeCondition = (employeeId, conditionId, data) =>
  aiEmployeeApi.patch(`/ai-employees/${employeeId}/wake-conditions/${conditionId}`, data);
export const deleteWakeCondition = (employeeId, conditionId) =>
  aiEmployeeApi.delete(`/ai-employees/${employeeId}/wake-conditions/${conditionId}`);
// Watchers v1 (agent_docs/triggers/WATCHERS_V1_CONTRACT.md §9). Test runs the STORED watch file once (nothing is
// sent) → { run_id }; poll getWatcherRun until `finished_at`. Runs → { runs: [WatcherRun], newest first }.
// Activation stays on updateWakeCondition({ status: 'active' }): 409 + `detail` when this version has not passed a Test.
export const runWatcherTest = (employeeId, conditionId) =>
  aiEmployeeApi.post(
    `/ai-employees/${encodeURIComponent(employeeId)}/wake-conditions/${encodeURIComponent(conditionId)}/test`
  );
export const listWatcherRuns = (employeeId, conditionId, limit = 20) =>
  aiEmployeeApi.get(
    `/ai-employees/${encodeURIComponent(employeeId)}/wake-conditions/${encodeURIComponent(conditionId)}/runs`,
    { params: { limit } }
  );
export const getWatcherRun = (employeeId, conditionId, runId) =>
  aiEmployeeApi.get(
    `/ai-employees/${encodeURIComponent(employeeId)}/wake-conditions/${encodeURIComponent(conditionId)}/runs/${encodeURIComponent(runId)}`
  );

// ===== Sharing (org team access — full parity) =====
// GET returns { organization_id, shared_with: [user_id], org_members: [{user_id, name, email, is_shared}] }.
// organization_id null ⇒ personal employee (sharing unavailable). PUT is a full-set replace.
export const getEmployeeSharing = (employeeId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/sharing`);
export const updateEmployeeSharing = (employeeId, userIds) =>
  aiEmployeeApi.put(`/ai-employees/${employeeId}/sharing`, { user_ids: userIds });
// Org-wide visibility (agent_docs/employee_org_visibility_prd.md): the owner's "Share with my managers" switch rides
// the same PUT (no user_ids = the share list stays as it is).
export const updateEmployeeShareWithManagers = (employeeId, on) =>
  aiEmployeeApi.put(`/ai-employees/${employeeId}/sharing`, { share_with_managers: Boolean(on) });
// The public card any member of the employee's organization may open (404 otherwise), and the two R4 actions:
// ask the owner + supervisor for access (one email), and the email's one-click share (anyone who can operate it).
export const getEmployeeProfile = (employeeId) =>
  aiEmployeeApi.get(`/ai-employees/${encodeURIComponent(employeeId)}/profile`);
export const requestEmployeeAccess = (employeeId) =>
  aiEmployeeApi.post(`/ai-employees/${encodeURIComponent(employeeId)}/request-access`);
export const addEmployeeShare = (employeeId, userId) =>
  aiEmployeeApi.post(`/ai-employees/${encodeURIComponent(employeeId)}/sharing/add`, { user_id: userId });
// Organizations v3 ring 4: hand the employee to another member of its organization (owner or org admin).
export const handOverEmployee = (employeeId, newOwnerUserId, keepPreviousAsShared) =>
  aiEmployeeApi.post(`/ai-employees/${employeeId}/handover`, {
    new_owner_user_id: newOwnerUserId,
    keep_previous_as_shared: Boolean(keepPreviousAsShared),
  });
// Move to organization (agent_docs/employee_org_move/PLAN.md): the owner moves ONE employee into another organization
// they administer. The plan read writes nothing: { destinations: [{id, name, domain}], plan: null | plan_move }; omit
// toOrgId to list destinations only. The move returns the service's per-employee result (the note is quiet).
export const getEmployeeOrgMovePlan = (employeeId, toOrgId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/org-move/plan`, { params: toOrgId ? { to_org: toOrgId } : {} });
export const moveEmployeeToOrg = (employeeId, toOrgId, reason) =>
  aiEmployeeApi.post(`/ai-employees/${employeeId}/org-move`, { to_org: toOrgId, reason: reason || null });

// ===== Meeting Presence (CEL-2596 B3) — the owner's side of the employee attending meetings =====
// Settings: { settings: {enabled, recording_default, live_voice, do_not_say}, voices, default_voice,
// disclosure: {display_name, intro}, employee_email, employee_name }. PUT is a partial update. (invite_policy retired
// Sep 22 2026, CEL-2740: who the employee meets is its own judgment; meetings are one on/off switch.)
export const getEmployeeMeetingSettings = (employeeId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/meeting-settings`);
export const updateEmployeeMeetingSettings = (employeeId, patch) =>
  aiEmployeeApi.put(`/ai-employees/${employeeId}/meeting-settings`, patch);
// tab: 'upcoming' | 'live' | 'past' → { tab, sessions: [card] } (card-slim; the detail owns the full body).
export const listEmployeeMeetings = (employeeId, tab = 'upcoming', limit = 50) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/meetings`, { params: { tab, limit } });
// { session, preparation, windows (last 50, oldest first), utterances (last 50) }
export const getEmployeeMeeting = (employeeId, sessionId) =>
  aiEmployeeApi.get(`/ai-employees/${employeeId}/meetings/${sessionId}`);
// An owner chat message "Meeting brief: {text}" into the employee's chat (the mind turns it into a brief).
export const whisperEmployeeMeeting = (employeeId, sessionId, text) =>
  aiEmployeeApi.post(`/ai-employees/${employeeId}/meetings/${sessionId}/whisper`, { text });
export const muteEmployeeMeeting = (employeeId, sessionId, muted) =>
  aiEmployeeApi.post(`/ai-employees/${employeeId}/meetings/${sessionId}/mute`, { muted });
export const endEmployeeMeeting = (employeeId, sessionId) =>
  aiEmployeeApi.post(`/ai-employees/${employeeId}/meetings/${sessionId}/end`, {});


export default aiEmployeeApi;
